import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { adminApi } from '../api';

function applyPromptPayload(data, setForm, setHasEmbeddedContract, setDefaults) {
  setForm({
    systemMessage: data.systemMessage || '',
    productDataPrefix: data.productDataPrefix || '',
    rulesText: Array.isArray(data.rules) ? data.rules.join('\n') : '',
  });
  setHasEmbeddedContract(Boolean(data.hasEmbeddedOutputContract));
  if (data.defaults) setDefaults(data.defaults);
}

export default function PromptPage() {
  const [form, setForm] = useState({
    systemMessage: '',
    productDataPrefix: '',
    rulesText: '',
  });
  const [defaults, setDefaults] = useState(null);
  const [hasEmbeddedContract, setHasEmbeddedContract] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.getPrompt().then((data) => {
      applyPromptPayload(data, setForm, setHasEmbeddedContract, setDefaults);
    }).catch((err) => setError(err.message));
  }, []);

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      const data = await adminApi.savePrompt({
        systemMessage: form.systemMessage,
        productDataPrefix: form.productDataPrefix,
        rules: form.rulesText.split('\n').map((line) => line.trim()).filter(Boolean),
      });
      applyPromptPayload(data, setForm, setHasEmbeddedContract, setDefaults);
      setMessage('Prompt salvato. La chat userà la nuova configurazione.');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCleanFormat = async () => {
    setError('');
    setMessage('');
    try {
      const data = await adminApi.cleanPromptFormat();
      applyPromptPayload(data, setForm, setHasEmbeddedContract, setDefaults);
      setMessage('Istruzioni JSON rimosse. Il formato di risposta resta gestito dal sistema.');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestoreDefaults = () => {
    if (!defaults) return;
    setForm({
      systemMessage: defaults.systemMessage || '',
      productDataPrefix: defaults.productDataPrefix || '',
      rulesText: Array.isArray(defaults.rules) ? defaults.rules.join('\n') : '',
    });
    setMessage('Testo di default caricato. Premi Salva per applicarlo.');
    setError('');
  };

  return (
    <Box component="form" onSubmit={handleSave}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Prompt AI
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Qui modifichi tono, personalità e regole di comportamento. Usa {'{{companyName}}'} per il nome azienda.
        Link e media dei prodotti non dipendono da questo testo: il sistema li prende dal catalogo.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Il formato JSON della risposta e i pulsanti Media sono bloccati nel codice.
        Non inserire schemi JSON, campi urlImage/urlVideo/url3Dviewer o istruzioni sul formato di uscita.
      </Alert>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {hasEmbeddedContract && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={handleCleanFormat}>
              Rimuovi schema JSON
            </Button>
          )}
        >
          Nel system message c’è ancora il vecchio schema JSON. Puoi toglierlo in sicurezza: il formato è già gestito dal sistema.
        </Alert>
      )}
      <TextField
        label="Personalità e tono"
        value={form.systemMessage}
        onChange={(e) => setForm((prev) => ({ ...prev, systemMessage: e.target.value }))}
        fullWidth
        multiline
        minRows={8}
        helperText="Descrivi chi è l’assistente e come deve parlare. Non toccare il formato di risposta."
        sx={{ mb: 2 }}
      />
      <TextField
        label="Regole di comportamento (una per riga)"
        value={form.rulesText}
        onChange={(e) => setForm((prev) => ({ ...prev, rulesText: e.target.value }))}
        fullWidth
        multiline
        minRows={10}
        helperText="Cosa deve o non deve fare. Evita istruzioni su JSON, URL o media."
        sx={{ mb: 2 }}
      />
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Formato di risposta (non modificabile)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              L’assistente risponde sempre in JSON con text, products e prompt.
              In products mette solo il nome (e il codice) del catalogo.
              Link, immagini, video e viewer 3D vengono aggiunti dal sistema dal prodotto reale.
              Se il prompt viene modificato, Visualizza Media continua a funzionare.
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Avanzate</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            label="Prefisso catalogo"
            value={form.productDataPrefix}
            onChange={(e) => setForm((prev) => ({ ...prev, productDataPrefix: e.target.value }))}
            fullWidth
            helperText="Testo che precede l’elenco prodotti inviato all’AI."
          />
        </AccordionDetails>
      </Accordion>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button type="submit" variant="contained">
          Salva prompt
        </Button>
        <Button type="button" variant="outlined" onClick={handleRestoreDefaults} disabled={!defaults}>
          Carica default
        </Button>
      </Box>
    </Box>
  );
}
